// Security utilities to prevent data leakage and code injection

if (process.env.NODE_ENV === "production") {
  // Silence all console output in production
  console.warn = () => {};
  console.error = () => {};
}

// Sanitize data for logging
const sanitizeForLogging = (data) => {
  if (typeof data === "object" && data !== null) {
    return sanitizeObject(data);
  }
  if (typeof data === "string" && containsSensitiveData(data)) {
    return maskSensitiveString(data);
  }
  return data;
};

// Check if string contains sensitive data
const containsSensitiveData = (str) => {
  const sensitivePatterns = [
    /password/i,
    /token/i,
    /secret/i,
    /key/i,
    /credential/i,
    /private/i,
    /internal/i,
    /admin/i,
  ];

  return sensitivePatterns.some((pattern) => pattern.test(str));
};

// Mask sensitive strings
const maskSensitiveString = (str) => {
  if (str.length > 4) {
    return (
      str.substring(0, 2) +
      "*".repeat(str.length - 4) +
      str.substring(str.length - 2)
    );
  }
  return "***";
};

// Sanitize objects for logging
const sanitizeObject = (obj) => {
  if (Array.isArray(obj)) {
    return obj.map((item) => sanitizeObject(item));
  }

  if (typeof obj === "object" && obj !== null) {
    const sanitized = {};

    for (const [key, value] of Object.entries(obj)) {
      if (isSensitiveKey(key)) {
        sanitized[key] = maskSensitiveString(String(value));
      } else {
        sanitized[key] = sanitizeObject(value);
      }
    }

    return sanitized;
  }

  return obj;
};

// Check if key is sensitive
const isSensitiveKey = (key) => {
  const sensitiveKeys = [
    "password",
    "token",
    "secret",
    "key",
    "credential",
    "private",
    "internal",
    "admin",
    "debug",
    "test",
    "authorization",
    "auth",
    "session",
    "cookie",
  ];

  return sensitiveKeys.some((sensitive) =>
    key.toLowerCase().includes(sensitive)
  );
};

// Prevent eval and Function constructor usage
const preventCodeInjection = () => {
  if (process.env.NODE_ENV === "production") {
    // Override eval to prevent code injection
    window.eval = () => {
      throw new Error("eval() is disabled for security reasons");
    };

    // Override Function constructor
    window.Function = () => {
      throw new Error("Function constructor is disabled for security reasons");
    };
  }
};

// Sanitize user input
const sanitizeInput = (input) => {
  if (typeof input !== "string") return input;

  // Remove potentially dangerous characters
  return input
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, "")
    .replace(/javascript:/gi, "")
    .replace(/on\w+\s*=/gi, "")
    .replace(/<iframe\b[^<]*(?:(?!<\/iframe>)<[^<]*)*<\/iframe>/gi, "")
    .trim();
};

// Validate API responses
const validateApiResponse = (response) => {
  // Check for suspicious patterns in response
  const suspiciousPatterns = [
    /<script/i,
    /javascript:/i,
    /eval\(/i,
    /Function\(/i,
  ];

  const responseStr = JSON.stringify(response);

  for (const pattern of suspiciousPatterns) {
    if (pattern.test(responseStr)) {
      throw new Error("Suspicious content detected in API response");
    }
  }

  return response;
};

// Initialize security measures
const initializeSecurity = () => {
  preventCodeInjection();

  // Monitor for suspicious DOM modifications
  if (process.env.NODE_ENV === "production") {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            const scripts = node.querySelectorAll("script");
            if (scripts.length > 0) {
              console.warn("Suspicious script injection detected");
            }
          }
        });
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }
};

// Security utility functions

/**
 * Mask sensitive data like tokens for logging
 * @param {string} data - The data to mask
 * @param {number} visibleChars - Number of characters to show at start and end
 * @returns {string} - Masked data
 */
export const maskSensitiveData = (data, visibleChars = 4) => {
  if (!data || typeof data !== "string") {
    return "***";
  }

  if (data.length <= visibleChars * 2) {
    return "*".repeat(data.length);
  }

  const start = data.substring(0, visibleChars);
  const end = data.substring(data.length - visibleChars);
  const middle = "*".repeat(data.length - visibleChars * 2);

  return `${start}${middle}${end}`;
};

/**
 * Mask JWT token for logging
 * @param {string} token - JWT token
 * @returns {string} - Masked token
 */
export const maskJWTToken = (token) => {
  if (!token || typeof token !== "string") {
    return "***";
  }

  // For JWT tokens, show first 10 and last 10 characters
  return maskSensitiveData(token, 10);
};

/**
 * Mask user data for logging
 * @param {object} userData - User data object
 * @returns {object} - Masked user data
 */
export const maskUserData = (userData) => {
  if (!userData || typeof userData !== "object") {
    return userData;
  }

  const masked = { ...userData };

  // Mask sensitive fields
  if (masked.email) {
    const [localPart, domain] = masked.email.split("@");
    if (localPart && domain) {
      const maskedLocal =
        localPart.length > 2
          ? localPart.substring(0, 2) + "*".repeat(localPart.length - 2)
          : "*".repeat(localPart.length);
      masked.email = `${maskedLocal}@${domain}`;
    }
  }

  if (masked.id) {
    masked.id = maskSensitiveData(masked.id.toString(), 4);
  }

  return masked;
};

export {
  sanitizeInput,
  validateApiResponse,
  initializeSecurity,
  sanitizeForLogging,
};
